import { FunctionComponent, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import {
	faCheck,
	faClock,
	faHeart,
	faHeartCrack,
	faUserSlash,
} from "@fortawesome/pro-solid-svg-icons";

import { MANUAL_PROSPECT_SENTIMENTS, ProspectInterest } from "@/enum/prospect.enum";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { classNames } from "@/utils/classNames";
import { useUpdateConversationSentimentMutation } from "@/pages/Private/redux/conversation/conversation.api";
import { Icon } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { useCloseOnOutsideClick } from "@/utils/useCloseOnOutsideClick";
import { runOrToast } from "@/utils/runOrToast";

export const Sentiment: FunctionComponent<{
	conversation: Conversation;
	/**
	 * Where the picker is portalled. Inside a lead panel this must be the panel itself: a
	 * `document.body` portal makes a pick read as a click outside the panel, which closes it and
	 * the picker with it before Save can be pressed (PREC-4445, like PREC-3627 and PREC-4168).
	 */
	portalContainer?: HTMLElement | null;
}> = ({ conversation, portalContainer }) => {
	const { t } = useTranslation();
	const [isVisible, setIsVisible] = useState(false);
	// The sentiment the chip shows. A save answers with the stored value at once, because the
	// reply this component was handed comes from whichever cache rendered the panel and is not
	// necessarily refreshed by the save (PREC-4445).
	const [shownSentiment, setShownSentiment] = useState<ProspectInterest | null | undefined>(
		conversation?.sentiment
	);

	useEffect(() => {
		setShownSentiment(conversation?.sentiment);
	}, [conversation?.sentiment]);

	const [updateConversationSentiment] = useUpdateConversationSentimentMutation();
	const [sentiment, setSentiment] = useState<ProspectInterest>(
		conversation?.sentiment || ProspectInterest.NEUTRAL
	);

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	useCloseOnOutsideClick(tooltipRef, isVisible, setIsVisible);

	useEffect(() => {
		if (isVisible && triggerRef.current && tooltipRef.current) {
			const triggerRect = triggerRef.current.getBoundingClientRect();

			// Viewport coordinates for a `position: fixed` node (web rule §11). An
			// absolutely positioned body portal adds its box to the document's scrollable
			// height, and html/body do not clip — so opening this popover low on the page
			// made the whole app scrollable (PREC-4114).
			const top = triggerRect.top;

			const newTop = top + 40 + 250;

			const consideredWindowHeight = window.innerHeight - 48;

			if (newTop > consideredWindowHeight) {
				tooltipRef.current.style.top = `${top - 258}px`;
			} else {
				tooltipRef.current.style.top = `${top + 40}px`;
			}

			tooltipRef.current.style.left = `${triggerRect.left + 32}px`;
		}
	}, [isVisible]);

	// A second click while the save is in flight is ignored: each save re-triggers the reply's CRM
	// campaign response (web CLAUDE.md §24c).
	const isSavingRef = useRef(false);
	const [isSaving, setIsSaving] = useState(false);

	const saveSentiment = async () => {
		if (isSavingRef.current) {
			return;
		}

		isSavingRef.current = true;
		setIsSaving(true);

		const saved = await runOrToast(
			() => updateConversationSentiment({ id: conversation.id, sentiment }).unwrap(),
			t("inbox.sentimentSaveFailed")
		);

		isSavingRef.current = false;
		setIsSaving(false);

		if (!saved) {
			return;
		}

		setShownSentiment(sentiment);
		setIsVisible(false);
	};

	const renderSentimentResult = () => {
		return ReactDOM.createPortal(
			<div
				ref={tooltipRef}
				className="fixed flex flex-col z-100 p-4 gap-4 bg-white rounded-lg shadow-lg z-[199] min-w-[300px] h-fit"
			>
				<div className="flex flex-col gap-2">
					{MANUAL_PROSPECT_SENTIMENTS.map((s, index) => (
						<div
							key={`${s}-${index}`}
							className={classNames(
								"cursor-pointer border border-transparent box-content hover:border hover:border-[inset] rounded-lg text-sm font-normal px-2 py-1 flex flex-row gap-1 items-center",
								s === sentiment ? "!border-blue-action !border" : "",
								s === ProspectInterest.INTERESTED
									? "bg-status-success-light text-status-success hover:border-status-success"
									: s === ProspectInterest.NOT_INTERESTED
										? "bg-status-error-light text-status-error hover:border-status-error"
										: s === ProspectInterest.NEUTRAL
											? "bg-status-info-light text-status-info hover:border-status-info"
											: s === ProspectInterest.BAD_TIMING
												? "bg-status-warning-light text-status-warning hover:border-status-warning"
												: s === ProspectInterest.WRONG_PERSON
													? "bg-status-wrong-person-light text-status-wrong-person hover:border-status-wrong-person"
													: "bg-status-info-light text-status-info hover:border-status-info"
							)}
							onClick={() => setSentiment(s)}
						>
							{s === ProspectInterest.INTERESTED && (
								<Icon
									className="w-5 h-5 mr-1 bg-status-success-light rounded-full p-1 text-status-success"
									icon={faHeart}
								/>
							)}
							{s === ProspectInterest.NOT_INTERESTED && (
								<Icon
									className="w-5 h-5 mr-1 bg-status-error-light rounded-full p-1 text-status-error"
									icon={faHeartCrack}
								/>
							)}
							{s === ProspectInterest.NEUTRAL && (
								<Icon
									className="w-5 h-5 mr-1 bg-status-info-light rounded-full p-1 text-status-info"
									icon={faCheck}
								/>
							)}
							{s === ProspectInterest.BAD_TIMING && (
								<Icon
									className="w-5 h-5 mr-1 bg-status-warning-light rounded-full p-1 text-status-warning"
									icon={faClock}
								/>
							)}
							{s === ProspectInterest.WRONG_PERSON && (
								<Icon
									className="w-5 h-5 mr-1 bg-status-wrong-person-light rounded-full p-1 text-status-wrong-person"
									icon={faUserSlash}
								/>
							)}
							{s.replaceAll("_", " ").capitalizeString()}
						</div>
					))}

					<Button
						color={ButtonColor.ACTION}
						isLoading={isSaving}
						size={ButtonSize.XS}
						title="Save"
						onClick={() => saveSentiment()}
					/>
				</div>
			</div>,
			portalContainer ?? document.body
		);
	};

	return (
		<div className="flex flex-row gap-2 w-fit mt-2">
			{isVisible && renderSentimentResult()}
			<div
				ref={triggerRef}
				className={classNames(
					"w-fit text-xs bg-white text-text-meta font-medium rounded-lg px-2 py-1 cursor-pointer border border-transparent hover:bg-brand-white/50 box-content hover:border border-[inset] flex flex-row gap-1 items-center",
					shownSentiment === ProspectInterest.INTERESTED
						? "text-status-success hover:border-status-success"
						: shownSentiment === ProspectInterest.NOT_INTERESTED
							? "text-status-error hover:border-status-error"
							: shownSentiment === ProspectInterest.NEUTRAL
								? "text-status-info hover:border-status-info"
								: shownSentiment === ProspectInterest.BAD_TIMING
									? "text-status-warning hover:border-status-warning"
									: shownSentiment === ProspectInterest.WRONG_PERSON
										? "text-status-wrong-person hover:border-status-wrong-person"
										: "bg-status-info-light text-status-info"
				)}
				onClick={(e) => {
					e.preventDefault();
					e.stopPropagation();
					setIsVisible?.(!isVisible);
				}}
			>
				{" "}
				{shownSentiment === ProspectInterest.INTERESTED && (
					<Icon
						className="w-5 h-5 mr-1 bg-status-success-light rounded-full p-1 text-status-success"
						icon={faHeart}
					/>
				)}
				{shownSentiment === ProspectInterest.NOT_INTERESTED && (
					<Icon
						className="w-5 h-5 mr-1 bg-status-error-light rounded-full p-1 text-status-error"
						icon={faHeartCrack}
					/>
				)}
				{shownSentiment === ProspectInterest.NEUTRAL && (
					<Icon
						className="w-5 h-5 mr-1 bg-status-info-light rounded-full p-1 text-status-info"
						icon={faCheck}
					/>
				)}
				{shownSentiment === ProspectInterest.BAD_TIMING && (
					<Icon
						className="w-5 h-5 mr-1 bg-status-warning-light rounded-full p-1 text-status-warning"
						icon={faClock}
					/>
				)}
				{shownSentiment === ProspectInterest.WRONG_PERSON && (
					<Icon
						className="w-5 h-5 mr-1 bg-status-wrong-person-light rounded-full p-1 text-status-wrong-person"
						icon={faUserSlash}
					/>
				)}
				Marked as{" "}
				<span
					className={classNames(
						"font-semibold rounded-lg px-2 py-1 mx-1",
						shownSentiment === ProspectInterest.INTERESTED
							? "bg-status-success-light text-status-success"
							: shownSentiment === ProspectInterest.NOT_INTERESTED
								? "bg-status-error-light text-status-error"
								: shownSentiment === ProspectInterest.NEUTRAL
									? "bg-status-info-light text-status-info"
									: shownSentiment === ProspectInterest.BAD_TIMING
										? "bg-status-warning-light text-status-warning"
										: shownSentiment === ProspectInterest.WRONG_PERSON
											? "bg-status-wrong-person-light text-status-wrong-person"
											: "bg-status-info-light text-status-info"
					)}
				>
					{shownSentiment?.replaceAll("_", " ")?.capitalizeString()}
				</span>{" "}
				by Signalist
				<Icon className="w-3 h-3 ml-1" icon={faChevronDown} />
			</div>
		</div>
	);
};
